import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import ReactSelect from '@base/react-select-global'
import { IValueEditorProps } from '@app-types/general'
import { createLogger } from '@utils/logger'
import Link from '@components/Link'
import useValueEditor from '@hooks/useValueEditor'
import { useViewContext } from '@base/ViewContext'
import { selectFieldSelectOptionsByFieldId } from '@reducers/data/field_select_option'

const FieldOptionsManager = React.lazy(() => import('@features/field_options_manager/FieldOptionsManager'))

// Initialize logger
const log = createLogger('SingleSelectEditor')
log.setLevel('info')

// Define interfaces for props and related data structures

type SingleSelectEditorProps = Omit<IValueEditorProps, 'serializeValue' | 'parseValue'> & {
  value: number | null
  serializeValue?: (value: number | null) => number | null
  parseValue?: (value: number | null) => number | null
  tableId: string | number
  fieldId?: string
}

const passthrough = (value: number | null): number | null => (value ? value : null)
const defaultParseValueFn = passthrough
const defaultSerializeValueFn = passthrough

// Convert between react-select option objects and number[] (IDs)
type OptionType = { value: number; label: string }
const toSelectOptions = (options: any[]): OptionType[] => options.map(o => ({ value: o.id, label: o.label }))
// Single-select helpers
const findSelectedOption = (id: number | string | null | undefined, options: OptionType[]): OptionType | null => {
  if (id === null || id === undefined) return null
  const coerced = typeof id === 'string' ? parseInt(id, 10) : id
  if (!Number.isFinite(coerced as number)) return null
  return options.find(opt => opt.value === (coerced as number)) || null
}
type ViewContextValueType = {
  tableId?: string
}

const SingleSelectEditor: React.FC<SingleSelectEditorProps> & { EDITOR_TYPE: string } = props => {
  const {
    name,
    value = null,
    onChange,
    parseValue = defaultParseValueFn,
    serializeValue = defaultSerializeValueFn,
    tableId: propTableId,
    fieldId,
    ...restProps
  } = props
  // Get some props from context, if not passed as props
  const context = useViewContext<ViewContextValueType>()
  const tableId = propTableId ?? context.tableId

  if (!fieldId) {
    log.warn('FieldId prop is missing for SingleSelectEditor. This is required to load field options.')
  }

  const rawOptions = fieldId ? useSelector((state: any) => selectFieldSelectOptionsByFieldId(state, fieldId)) : []
  const options = rawOptions.map(opt => ({ id: Number(opt.id), label: opt.attributes.label }))

  const { localValue, handleLocalChange } = useValueEditor<number | null, number | null>({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
  })

  const handleSelectChange = (opt: OptionType | null) => {
    const id = opt?.value || null
    handleLocalChange(id)
  }

  const getOptionManagerLinkContent = () => {
    if (fieldId && tableId) {
      return (
        <div className="select-editor__option-manager-link">
          <React.Suspense fallback={<span className="muted">Loading…</span>}>
            <FieldOptionsManager
              tableId={tableId}
              fieldId={fieldId}
              autoOpen={false}
              onLoadingChange={setOptionsLoading}
              trigger={open => (
                <Link
                  onClick={e => {
                    e.preventDefault()
                    open()
                  }}
                  href="#"
                >
                  Manage field options
                </Link>
              )}
            />
          </React.Suspense>
        </div>
      )
    } else {
      log.warn(
        'TableId and FieldId props are both missing for SingleSelectEditor. TableId is required to load field options.'
      )
    }
    return null
  }
  // Track loading state of options (provided by FieldOptionsManager)
  const [isOptionsLoading, setOptionsLoading] = useState(false)

  const selectOptions = toSelectOptions(options)
  const selectedOption = findSelectedOption(localValue as any, selectOptions)

  log.debug('Rendering SingleSelectEditor', {
    name,
    localValue,
    selectedOption,
    selectOptions,
  })

  return (
    <span className="content-editor content-editor--single-select single-select-editor select-editor">
      <div className="select-editor__option-selection">
        <ReactSelect
          name={name}
          options={selectOptions}
          value={selectedOption}
          isLoading={isOptionsLoading}
          onChange={handleSelectChange}
          placeholder="Select option..."
          {...(restProps as any)}
        />
      </div>
      {getOptionManagerLinkContent()}
    </span>
  )
}

SingleSelectEditor.EDITOR_TYPE = 'single-select'

export default SingleSelectEditor
